import cl from 'classnames';

import { IconsEnum } from '../../../../assets/icons/types';
import { PureButtonHelper } from '../../../../helpers/buttons/PureButtonHelper';
import { PureIconButtonHelper } from '../../../../helpers/buttons/PureIconButtonHelper';
import { PureTooltipIconButtonHelper } from '../../../../helpers/buttons/PureTooltipIconButtonHelper';
import { StaticImageHelper } from '../../../../helpers/StaticImageHelper';
import { Translate } from '../../../../helpers/Translate';

import { composerKeys, words } from '../../../../locales/keys';
import { TooltipPlacement } from '../../../../helpers/tooltips/tooltipsConstants';

import { RenderGenerationStatus } from '../../../renderGenerations/renderGenerationsTypes';
import { LifestyleGenerationStatus } from '../../../lifestyleGenerations/lifestyleGenerationsTypes';
import { CompositeRenderGenerationItem } from '../../hooks/useComposerRenderGeneration';
import { ComposerLifestyleGenerationItem } from '../../hooks/useComposerLifestyleGeneration';
import { buildDownloadName } from '../../../modelViewers/components/content/ModelViewerEditor/utils/buildDownloadName';
import { downloadFile } from '../../../../utils/downloadFile';

export type ComposerGenerationItem =
  | CompositeRenderGenerationItem
  | ComposerLifestyleGenerationItem;

interface ComposerRenderGenerationCardProps {
  item: ComposerGenerationItem;
  isActive: boolean;
  sceneName?: string;
  onSelectView: (id: string) => void;
  onRemoveRender: (localId: string) => void;
  onStopRender: (localId: string) => void;
}

function ComposerRenderGenerationCard({
  item,
  isActive,
  sceneName,
  onSelectView,
  onRemoveRender,
  onStopRender
}: ComposerRenderGenerationCardProps) {
  const isLifestyle = item.kind === 'lifestyle';
  const isLoading = isLifestyle
    ? item.status === LifestyleGenerationStatus.Pending ||
      item.status === LifestyleGenerationStatus.Processing ||
      item.status === LifestyleGenerationStatus.PreparingSource
    : item.status === RenderGenerationStatus.Pending ||
      item.status === RenderGenerationStatus.Processing;
  const isDone = isLifestyle
    ? item.status === LifestyleGenerationStatus.Success
    : item.status === RenderGenerationStatus.Completed;
  const isCancelled =
    !isLifestyle && item.status === RenderGenerationStatus.Cancelled;
  const generationModeI18nText = isLifestyle
    ? composerKeys.generationModeLifestyle
    : composerKeys.generationModeCompositeRender;
  const downloadType = isLifestyle ? 'lifestyle' : 'composite-render';

  return (
    <div
      onClick={() => (isLoading || isDone) && onSelectView(item.localId)}
      className={cl(
        'rounded-xl border overflow-hidden bg-white dark:bg-gray-900 transition-colors',
        {
          'cursor-pointer': isLoading || isDone,
          'border-blue-400 dark:border-blue-500': isActive || isLoading,
          'border-gray-200 dark:border-gray-700': !isActive && !isLoading
        }
      )}
    >
      {isLoading ? (
        <div className="relative">
          <div className="aspect-w-16 aspect-h-9 w-full bg-cosmos-blue animate-gradient-angle" />
          <span className="absolute top-1.5 left-1.5 z-10 px-1.5 py-0.5 rounded text-2xs font-medium bg-black/50 text-white">
            <Translate id={generationModeI18nText} />
          </span>
        </div>
      ) : isDone && item.resultPreviewUrl ? (
        <div className="relative">
          <StaticImageHelper
            src={item.resultPreviewUrl}
            alt=""
            className="w-full h-auto block"
          />
          <span className="absolute top-1.5 left-1.5 px-1.5 py-0.5 rounded text-2xs font-medium bg-black/50 text-white">
            <Translate id={generationModeI18nText} />
          </span>
        </div>
      ) : isCancelled ? (
        <div className="aspect-w-16 aspect-h-9 w-full bg-gray-100 dark:bg-gray-800">
          <span className="absolute inset-0 text-xs text-gray-500 dark:text-gray-400 p-2 text-center flex items-center justify-center">
            <Translate id={composerKeys.renderCancelled} />
          </span>
        </div>
      ) : (
        <div className="aspect-w-16 aspect-h-9 w-full bg-red-100 dark:bg-red-900/20">
          <span className="absolute inset-0 text-xs text-red-500 p-2 text-center flex items-center justify-center">
            {item.errorMessage || (
              <Translate id={composerKeys.renderRequestFailed} />
            )}
          </span>
        </div>
      )}

      <div className="p-2 space-y-1.5">
        <div className="flex gap-1">
          {/* Stop (while generating) — only button visible during load */}
          {isLoading && (
            <PureButtonHelper
              i18nText={words.stop}
              className="py-1.5 px-3 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 flex-1 justify-center transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onStopRender(item.localId);
              }}
            />
          )}

          {/* Download */}
          {isDone && item.resultFullUrl && (
            <PureIconButtonHelper
              icon={IconsEnum.DOWNLOAD_SOLID}
              iconClassName="h-5 w-5"
              i18nText={words.download}
              i18nTextClassName="block"
              className="py-1.5 pl-1.5 pr-3 space-x-1 rounded-md inline-flex items-center whitespace-nowrap text-sm font-medium leading-5 focus:ring-base bg-gray-200 hover:bg-gray-300 text-gray-800 dark:text-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 flex-1 justify-center transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                downloadFile({
                  downloadFile: {
                    file: item.resultFullUrl,
                    name: buildDownloadName(
                      sceneName,
                      downloadType,
                      item.resultFullUrl
                    )
                  },
                  withBlob: true
                });
              }}
            />
          )}

          {/* Remove — hidden during generation, Stop replaces it */}
          {!isLoading && (
            <PureTooltipIconButtonHelper
              icon={IconsEnum.TRASH_SOLID}
              iconClassName="h-5 w-5"
              tooltipI18nText={words.remove}
              tooltipPlacement={TooltipPlacement.TOP}
              className="py-1.5 px-1.5 rounded-md inline-flex items-center leading-5 focus:ring-base text-white bg-red-500 hover:bg-red-600 shadow-sm transition-colors"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveRender(item.localId);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default ComposerRenderGenerationCard;
